import { avisoDePersonaRestaurada } from '../components/aviso-de-persona-restaurada';
import {
  EtiquetaDePagina,
  CLASE_TITULO_DE_PAGINA,
  CLASE_SUBTITULO_DE_PAGINA,
} from '@/components/patterns';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { ResetPasswordDialog } from '@/features/users/components/reset-password-dialog';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft,
  ArrowRightLeft,
  History,
  RotateCcw,
  UserCog,
  UserPlus,
  ShieldCheck,
  Ban,
  Building2,
  CalendarClock,
  CheckCircle,
  Clock,
  KeyRound,
  LogIn,
  Mail,
  ShieldAlert,
  Trash2,
  CalendarCog,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { adminService } from '../services/admin-service';
import { AccesosDeLaPersona } from '../components/accesos-de-la-persona';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { textoDelDestino } from '../lib/destino-de-soporte';
import type { DestinoDeSoporte, DetalleDeUsuario } from '../types/admin.types';

/**
 * Una persona, vista desde la plataforma.
 *
 * 🔴 No existía. Desde el listado global de usuarios no se podía **abrir** a
 * nadie: las 251 filas eran un callejón sin salida. Para saber algo de alguien
 * —de qué empresa es, si sigue entrando, qué le hicieron— había que entrar como
 * él, que es justo el rodeo que se está tratando de eliminar.
 *
 * Lo que más valor da acá es la última tarjeta: **qué se le hizo y quién lo
 * hizo.** Esos registros se venían guardando desde que existe la auditoría y no
 * se mostraban en ninguna pantalla.
 */

const ETIQUETAS_DE_ROL: Record<string, string> = {
  PLATFORM_ADMIN: 'Admin de plataforma',
  COMPANY_ADMIN: 'Admin de empresa',
  SALESPERSON: 'Vendedor',
  APPROVER: 'Aprobador',
};

/*
  Las acciones se guardan como `entidad.accion`; acá se dicen en castellano y se
  les da un ícono. El ícono no decora: en una lista de veinte entradas es lo que
  deja distinguir de un vistazo "le cambiaron la clave" de "entraron como él", que
  son cosas muy distintas.
*/
const ACCIONES: Record<string, { texto: string; icono: LucideIcon }> = {
  'user.password_reset': { texto: 'Le restablecieron la contraseña', icono: KeyRound },
  'user.update': { texto: 'Le cambiaron datos de la cuenta', icono: UserCog },
  'user.impersonate': { texto: 'Entraron como esta persona', icono: LogIn },
  'user.delete': { texto: 'Se eliminó esta cuenta', icono: Trash2 },
  'user.move_tenant': { texto: 'Lo movieron de empresa', icono: ArrowRightLeft },
  'user.move': { texto: 'Lo movieron de empresa', icono: ArrowRightLeft },
  'user.restore': { texto: 'Se restauró esta cuenta', icono: RotateCcw },
  'user.password_self': { texto: 'Cambió su contraseña', icono: KeyRound },
  'user.membership.grant': { texto: 'Le dieron acceso a una empresa', icono: UserPlus },
  'user.membership.update': { texto: 'Le cambiaron el acceso a una empresa', icono: UserCog },
  'user.membership.revoke': { texto: 'Le quitaron el acceso a una empresa', icono: Ban },
  'role_permission.update': { texto: 'Cambiaron los permisos de su rol', icono: ShieldCheck },
  'role_permission.reset': { texto: 'Restablecieron los permisos de su rol', icono: RotateCcw },
  'user.create': { texto: 'Se creó esta cuenta', icono: UserPlus },
};

function fecha(valor?: string | null): string {
  if (!valor) return '—';
  return new Date(valor).toLocaleString('es-BO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Dato({
  icono,
  etiqueta,
  children,
}: {
  icono: React.ReactNode;
  etiqueta: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 text-muted-foreground" aria-hidden="true">
        {icono}
      </span>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{etiqueta}</p>
        <div className="mt-0.5 break-words">{children}</div>
      </div>
    </div>
  );
}

export function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  // Impersonar pasa por el store: no alcanza con llamar a la API, hay que
  // reemplazar la sesión entera —token, permisos y menú— por la del otro.
  const impersonate = useAuthStore((s) => s.impersonate);

  const [datos, setDatos] = useState<DetalleDeUsuario | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resetDialog, setResetDialog] = useState<{
    id: string;
    nombre: string;
    email: string;
  } | null>(null);
  /*
    🔴 Cambiar la expiración vivía en el listado que esta ficha reemplazó, y se
    perdió en la mudanza: la ficha se quedó con «cambiar rol de plataforma» en su
    lugar. El endpoint nunca dejó de aceptarla (`updateUserSchema.expiresAt`),
    así que durante toda la rama el dato se podía ver pero no tocar desde ninguna
    pantalla alcanzable.

    `null` = el diálogo está cerrado. Un string —incluso vacío— es "abierto", y
    el vacío significa "sin límite": son estados distintos y por eso no alcanza
    con un booleano más una fecha.
  */
  const [expiracionEnEdicion, setExpiracionEnEdicion] = useState<string | null>(null);
  const [guardandoExpiracion, setGuardandoExpiracion] = useState(false);

  const cargar = useCallback(async () => {
    if (!id) return;
    setCargando(true);
    setError(null);
    try {
      setDatos(await adminService.getUserDetail(id));
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'No se pudo cargar este usuario.');
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const u = datos?.user;
  const nombre = u ? `${u.firstName} ${u.lastName}` : '';

  function alternarActivo() {
    if (!u) return;
    confirm({
      title: u.isActive ? `¿Desactivar a ${nombre}?` : `¿Activar a ${nombre}?`,
      description: u.isActive
        ? 'No va a poder entrar más. Sus datos y su historial quedan intactos.'
        : 'Vuelve a poder entrar con su contraseña actual.',
      confirmText: u.isActive ? 'Sí, desactivar' : 'Sí, activar',
      variant: u.isActive ? 'destructive' : 'default',
      onConfirm: async () => {
        try {
          await adminService.updateUser(u.id, { isActive: !u.isActive });
          toast({ title: u.isActive ? 'Usuario desactivado' : 'Usuario activado' });
          await cargar();
        } catch (err: any) {
          toast({
            variant: 'destructive',
            title: 'No se pudo cambiar',
            description: err?.response?.data?.error ?? 'Ocurrió un error.',
          });
        }
      },
    });
  }

  /*
    Ascender o bajar el rol de plataforma.

    🔴 Por qué ahora sí hay un botón, cuando el primer admin se crea con un script.

    El agujero de los 175 días era que **un admin de EMPRESA** se creaba un admin de
    plataforma. Que un admin de plataforma nombre a otro es distinto: no es escalar,
    es un otorgamiento lateral de alguien que ya tiene todo el poder — y es como
    funciona cualquier backoffice.

    Así que el reparto queda en dos tiempos: **el primero se crea con el script**
    —hace falta acceso a la base, es el arranque— y **de ahí en adelante se hace
    desde acá**. Nadie más tiene que entrar a la base para sumar a un compañero.

    Las dos formas de quedarse afuera las frena el servidor: no podés quitarte el rol
    a vos mismo, y no puede quedar ninguno.
  */
  function cambiarRolDePlataforma() {
    if (!u) return;
    const asciende = u.role !== 'PLATFORM_ADMIN';

    confirm({
      title: asciende
        ? `¿Hacer a ${nombre} administrador de plataforma?`
        : `¿Quitarle a ${nombre} el rol de plataforma?`,
      description: asciende
        ? `Va a ver y administrar TODAS las empresas de la plataforma, no solo ${u.tenant?.name ?? 'la suya'}. Es el acceso más amplio del sistema y queda registrado quién lo dio.`
        : `Vuelve a ser administrador de ${u.tenant?.name ?? 'su empresa'} y deja de ver las demás. Queda registrado quién lo hizo.`,
      confirmText: asciende ? 'Sí, ascender' : 'Sí, quitar',
      variant: asciende ? 'default' : 'destructive',
      onConfirm: async () => {
        try {
          await adminService.updateUser(u.id, {
            role: asciende ? 'PLATFORM_ADMIN' : 'COMPANY_ADMIN',
          });
          toast({
            title: asciende ? 'Ahora es administrador de plataforma' : 'Rol de plataforma quitado',
            /*
              El aviso del token importa: con la sesión abierta la persona sigue
              viendo lo de antes y cree que no funcionó.
            */
            description: `${nombre} tiene que cerrar sesión y volver a entrar para que le tome el cambio.`,
          });
          await cargar();
        } catch (err: any) {
          toast({
            variant: 'destructive',
            title: 'No se pudo cambiar el rol',
            description: err?.response?.data?.error ?? 'Ocurrió un error.',
          });
        }
      },
    });
  }

  /**
   * Fija o quita hasta cuándo puede entrar esta persona.
   *
   * `nuevaFecha` viene del campo `type="date"` (`YYYY-MM-DD`) o es `null` para
   * quitar el límite. Se manda `null` explícito y NO un string vacío ni el campo
   * ausente: el esquema del servidor distingue las tres cosas y sólo `null`
   * borra la fecha.
   */
  async function guardarExpiracion(nuevaFecha: string | null) {
    if (!u) return;
    setGuardandoExpiracion(true);
    try {
      await adminService.updateUser(u.id, {
        expiresAt: nuevaFecha ? new Date(nuevaFecha).toISOString() : null,
      });
      toast({
        title: nuevaFecha ? 'Se cambió hasta cuándo puede entrar' : 'Se le quitó el límite',
        description: nuevaFecha
          ? `${nombre} va a poder entrar hasta el ${new Date(nuevaFecha).toLocaleDateString('es-BO')}.`
          : `${nombre} puede entrar sin fecha de corte.`,
      });
      setExpiracionEnEdicion(null);
      await cargar();
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'No se pudo cambiar',
        description: err?.response?.data?.error ?? 'Ocurrió un error.',
      });
    } finally {
      setGuardandoExpiracion(false);
    }
  }

  async function entrarComo() {
    if (!u) return;
    // A3: se anuncia la empresa real; si no se puede entrar, se dice antes de preguntar.
    let destino: DestinoDeSoporte;
    try {
      destino = await adminService.destinoDeSoporte(u.id);
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'No se puede entrar',
        description: err?.response?.data?.error ?? 'Ocurrió un error.',
      });
      return;
    }
    confirm({
      title: `¿Entrar como ${nombre}?`,
      description: `Vas a ver Prixia como lo ve esta persona, con los datos de ${textoDelDestino(destino)}. Todo lo que hagas queda registrado a tu nombre.`,
      confirmText: 'Entrar',
      onConfirm: async () => {
        try {
          const slug = await impersonate(u.id);
          /*
            Recarga completa y con la empresa en la dirección.

            🔴 `navigate()` no sirve acá por dos razones que se suman: el
            router se montó con el basename del plano en el que estábamos
            —ninguno, si venimos del panel— así que el destino saldría sin
            empresa; y el basename sólo se vuelve a resolver en un arranque
            de la app. Una URL sin empresa deja a la persona trabajando en
            direcciones que no dicen en cuál está, que es justo lo que este
            frente vino a eliminar.
          */
          window.location.href = slug ? `/${slug}/dashboard` : '/dashboard';
        } catch (err: any) {
          toast({
            variant: 'destructive',
            title: 'No se pudo entrar',
            description: err?.response?.data?.error ?? 'Ocurrió un error.',
          });
        }
      },
    });
  }

  async function restaurar() {
    if (!u) return;
    try {
      const r = await adminService.restaurarUsuario(u.id);
      toast({
        title: `${nombre} volvió a la plataforma.`,
        description: avisoDePersonaRestaurada(r.accesosApagados),
      });
      await cargar();
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'No se pudo restaurar',
        description: err?.response?.data?.error ?? 'Ocurrió un error.',
      });
    }
  }

  function eliminar() {
    if (!u) return;
    /*
      L5 (1.3): eliminar LIBERA el correo y deja el historial. Se puede restaurar mientras
      nadie tome ese correo. Que conste en la pregunta.
    */
    confirm({
      title: `¿Eliminar a ${nombre}?`,
      description:
        'Deja de poder entrar a todas sus empresas y sale de los listados. Su historial queda. Su correo queda libre para una cuenta nueva. Se puede restaurar desde Personas › Personas eliminadas mientras nadie use ese correo.',
      confirmText: 'Sí, eliminar',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await adminService.deleteUser(u.id);
          toast({ title: `${nombre} fue eliminado.` });
          navigate('/plataforma/personas');
        } catch (err: any) {
          toast({
            variant: 'destructive',
            title: 'No se pudo eliminar',
            description: err?.response?.data?.error ?? 'Ocurrió un error.',
          });
        }
      },
    });
  }

  return (
    <AppLayout variante="plataforma">
      <div className="container mx-auto space-y-6 p-4 md:p-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/plataforma/personas')}
          className="-ml-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Volver a Personas
        </Button>

        {cargando ? (
          <div className="space-y-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-56" />
          </div>
        ) : error ? (
          <div className="space-y-3">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={cargar}>
              Reintentar
            </Button>
          </div>
        ) : u ? (
          <>
            {/* ── Identidad y acciones ── */}
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
                  {u.firstName[0]}
                  {u.lastName[0]}
                </div>
                <div>
                  <div className="mb-3">
                    <EtiquetaDePagina tono="neutro">Plataforma · Persona</EtiquetaDePagina>
                  </div>
                  <h1 className={CLASE_TITULO_DE_PAGINA}>{nombre}</h1>
                  <p className={`mt-2 ${CLASE_SUBTITULO_DE_PAGINA}`}>{u.email}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge variant={u.role === 'PLATFORM_ADMIN' ? 'destructive' : 'default'}>
                      {ETIQUETAS_DE_ROL[u.role] ?? u.role}
                    </Badge>
                    {u.customRole && <Badge variant="secondary">{u.customRole.name}</Badge>}
                    {u.isActive ? (
                      <span className="flex items-center gap-1 text-sm text-green-600">
                        <CheckCircle className="h-3.5 w-3.5" aria-hidden="true" /> Activo
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-sm text-destructive">
                        <Ban className="h-3.5 w-3.5" aria-hidden="true" /> Inactivo
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={entrarComo}>
                  <LogIn className="mr-2 h-4 w-4" aria-hidden="true" />
                  Entrar como
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setResetDialog({ id: u.id, nombre, email: u.email })}
                >
                  <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />
                  Restablecer contraseña
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    setExpiracionEnEdicion(u.expiresAt ? u.expiresAt.split('T')[0] : '')
                  }
                >
                  <CalendarCog className="mr-2 h-4 w-4" aria-hidden="true" />
                  Cambiar expiración
                </Button>
                {/*
                  Va antes de Desactivar y separado por su peso: es la acción que
                  más alcance tiene de toda la pantalla.
                */}
                <Button
                  variant="outline"
                  onClick={cambiarRolDePlataforma}
                  className={
                    u.role === 'PLATFORM_ADMIN'
                      ? undefined
                      : 'border-plataforma/50 text-plataforma hover:bg-plataforma-suave'
                  }
                >
                  <ShieldCheck className="mr-2 h-4 w-4" aria-hidden="true" />
                  {u.role === 'PLATFORM_ADMIN'
                    ? 'Quitar rol de plataforma'
                    : 'Ascender a plataforma'}
                </Button>
                <Button variant={u.isActive ? 'destructive' : 'default'} onClick={alternarActivo}>
                  {u.isActive ? (
                    <Ban className="mr-2 h-4 w-4" aria-hidden="true" />
                  ) : (
                    <CheckCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                  )}
                  {u.isActive ? 'Desactivar' : 'Activar'}
                </Button>
                <Button
                  variant="outline"
                  onClick={eliminar}
                  className="border-destructive/50 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  Eliminar
                </Button>
              </div>
            </div>

            {/* L5 (1.3): una cuenta eliminada se dice arriba de todo, con cómo devolverla. */}
            {u.deletedAt && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <div className="min-w-0 text-sm">
                  <p className="font-medium text-destructive">Eliminada el {fecha(u.deletedAt)}</p>
                  <p className="mt-0.5 text-muted-foreground">
                    Su correo era {u.originalEmail ?? '—'} y quedó libre. Su historial está intacto.
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={restaurar}>
                  <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
                  Restaurar
                </Button>
              </div>
            )}

            {/*
              El aviso va arriba de los datos y no al pie: es sobre la identidad de
              esta cuenta, no un detalle más.
            */}
            {datos.avisoDeEmpresaAjena && (
              <div className="flex items-start gap-3 rounded-md border border-plataforma/40 bg-plataforma-suave p-3">
                <ShieldAlert
                  className="mt-0.5 h-4 w-4 shrink-0 text-plataforma"
                  aria-hidden="true"
                />
                <div className="min-w-0 text-sm">
                  <p className="etiqueta-caps text-plataforma">Revisar</p>
                  <p className="mt-0.5 font-medium">
                    Este administrador de plataforma está dentro de una empresa que parece de un
                    cliente
                  </p>
                  <p className="mt-0.5 text-muted-foreground">{datos.avisoDeEmpresaAjena}</p>
                </div>
              </div>
            )}

            {/* ── Datos ── */}
            <Card>
              <CardContent className="grid gap-5 p-5 sm:grid-cols-2 lg:grid-cols-3">
                <Dato icono={<Building2 className="h-4 w-4" />} etiqueta="Empresa">
                  {u.tenant ? (
                    <Link
                      to={`/plataforma/empresas/${u.tenant.id}`}
                      className="text-primary hover:underline"
                    >
                      {u.tenant.name}
                    </Link>
                  ) : (
                    '—'
                  )}
                  {u.tenant && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {u.tenant._count?.users ?? 0} usuarios
                    </span>
                  )}
                </Dato>

                <Dato icono={<Mail className="h-4 w-4" />} etiqueta="Correo">
                  {u.email}
                </Dato>

                <Dato icono={<CalendarClock className="h-4 w-4" />} etiqueta="Acceso hasta">
                  {u.expiresAt ? (
                    <span
                      className={
                        new Date(u.expiresAt) < new Date() ? 'text-destructive' : undefined
                      }
                    >
                      {fecha(u.expiresAt)}
                      {new Date(u.expiresAt) < new Date() && ' (vencido)'}
                    </span>
                  ) : (
                    'Sin límite'
                  )}
                </Dato>

                <Dato icono={<LogIn className="h-4 w-4" />} etiqueta="Último ingreso">
                  {u.lastLoginAt ? fecha(u.lastLoginAt) : 'Nunca entró'}
                </Dato>

                <Dato icono={<Clock className="h-4 w-4" />} etiqueta="Última actividad">
                  {fecha(u.lastActiveAt)}
                </Dato>

                <Dato icono={<CalendarClock className="h-4 w-4" />} etiqueta="Se creó">
                  {fecha(u.createdAt)}
                </Dato>
              </CardContent>
            </Card>

            {/* ── Empresas a las que entra (L5 · 1.2) ── */}
            {u.role !== 'PLATFORM_ADMIN' && !u.deletedAt && (
              <AccesosDeLaPersona
                userId={u.id}
                nombre={nombre}
                membresias={datos.membresias ?? []}
                onCambio={cargar}
              />
            )}

            {/* ── Auditoría ── */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Qué se le hizo a esta cuenta</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Las últimas 20 acciones de administración sobre esta persona, con quién las hizo.
                </p>
              </CardHeader>
              <CardContent>
                {datos.historial.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    Nadie tocó esta cuenta desde el backoffice.
                  </p>
                ) : (
                  /*
                    Línea de tiempo y no lista.

                    Una lista de filas trata cada entrada como un ítem suelto; acá lo
                    que importa es la SECUENCIA — qué pasó antes de qué. La línea
                    vertical que une los nodos es lo que dice eso sin una palabra.

                    Va `relative` con la línea en `absolute` detrás de los nodos: si
                    la línea fuera un borde de cada ítem, se cortaría entre uno y otro
                    y dejaría de leerse como una sola línea.
                  */
                  <div className="relative">
                    <div
                      className="absolute left-[19px] top-3 bottom-3 w-px bg-border"
                      aria-hidden="true"
                    />

                    <ol className="relative space-y-4">
                      {datos.historial.map((h) => {
                        const info = ACCIONES[h.action] ?? { texto: h.action, icono: History };
                        const Icono = info.icono;
                        const enSoporte = !!h.impersonando;

                        return (
                          <li key={h.id} className="flex gap-4">
                            {/* El nodo. Su fondo tapa la línea justo detrás. */}
                            <span
                              className={cn(
                                'relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 bg-card',
                                enSoporte
                                  ? 'border-plataforma/40 text-plataforma'
                                  : 'border-border text-muted-foreground'
                              )}
                            >
                              <Icono className="h-4 w-4" aria-hidden="true" />
                            </span>

                            <div
                              className={cn(
                                'min-w-0 flex-1 rounded-md border p-3',
                                /*
                                  Lo hecho en modo soporte se marca con el color de
                                  plataforma y un borde izquierdo grueso: fuimos
                                  NOSOTROS actuando dentro de la sesión del cliente,
                                  y esa distinción es la razón de ser de la auditoría.
                                */
                                enSoporte
                                  ? 'border-plataforma/30 border-l-[3px] border-l-plataforma bg-plataforma-suave/60'
                                  : 'border-border bg-card'
                              )}
                            >
                              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                                <p className="font-medium">
                                  {info.texto}
                                  {enSoporte && (
                                    <span className="ml-2 inline-flex items-center gap-1 rounded bg-plataforma px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wider text-plataforma-foreground">
                                      <ShieldAlert className="h-3 w-3" aria-hidden="true" />
                                      modo soporte
                                    </span>
                                  )}
                                </p>
                                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                                  {fecha(h.createdAt)}
                                </span>
                              </div>

                              <p className="mt-1 text-sm text-muted-foreground">
                                {h.autor
                                  ? `${h.autor.firstName} ${h.autor.lastName} · ${h.autor.email}`
                                  : 'autor desconocido'}
                                {/*
                                  El nombre de arriba es el del administrador REAL. Se
                                  dice a nombre de quién estaba entrando, porque sin
                                  eso el registro parece decir que lo hizo el cliente.
                                */}
                                {enSoporte && (
                                  <span className="block text-xs">
                                    actuando dentro de la sesión de {h.impersonando!.firstName}{' '}
                                    {h.impersonando!.lastName}
                                  </span>
                                )}
                              </p>
                            </div>
                          </li>
                        );
                      })}

                      {/* El final de la línea, para que no termine en el aire. */}
                      <li className="flex items-center gap-4">
                        <span className="relative z-10 ml-[15px] h-2.5 w-2.5 shrink-0 rounded-full bg-border" />
                        <span className="etiqueta-caps">fin del registro</span>
                      </li>
                    </ol>
                  </div>
                )}
              </CardContent>
            </Card>

            <ResetPasswordDialog usuario={resetDialog} onClose={() => setResetDialog(null)} />

            <Dialog
              open={expiracionEnEdicion !== null}
              onOpenChange={(abierto) => {
                if (!abierto) setExpiracionEnEdicion(null);
              }}
            >
              <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                  <DialogTitle>Cambiar expiración de {nombre}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="expira-el">Hasta cuándo puede entrar</Label>
                    <Input
                      id="expira-el"
                      type="date"
                      value={expiracionEnEdicion ?? ''}
                      onChange={(e) => setExpiracionEnEdicion(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Pasada esa fecha deja de poder entrar. Su cuenta y su historial quedan
                      intactos.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      className="flex-1"
                      disabled={guardandoExpiracion || !expiracionEnEdicion}
                      onClick={() => guardarExpiracion(expiracionEnEdicion || null)}
                    >
                      Guardar
                    </Button>
                    {/*
                      Quitar el límite es su propia acción y no "guardar con el campo
                      vacío": son dos intenciones distintas y la de borrar tiene que
                      poder decirse en un solo gesto.
                    */}
                    <Button
                      variant="outline"
                      disabled={guardandoExpiracion || !u.expiresAt}
                      onClick={() => guardarExpiracion(null)}
                    >
                      Quitar límite
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </>
        ) : null}

        <ConfirmDialog />
      </div>
    </AppLayout>
  );
}

export default AdminUserDetailPage;
